function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React from "react";
import Portal from "@reach/portal";
import { checkStyles, wrapEvent, useForkedRef } from "@reach/utils";
import FocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";
import { string, func, bool } from "prop-types";

var noop = function noop() {}; ////////////////////////////////////////////////////////////////////////////////


export var DialogOverlay = React.forwardRef(function DialogOverlay(_ref, forwardedRef) {
  var _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? true : _ref$isOpen,
      props = _objectWithoutPropertiesLoose(_ref, ["isOpen"]);

  var ownRef = React.useRef(null);
  var ref = forwardedRef || ownRef;
  React.useEffect(function () {
    checkStyles("dialog");
  }, []);
  return isOpen ? React.createElement(Portal, {
    "data-reach-dialog-wrapper": true
  }, React.createElement(DialogInner, _extends({
    ref: ref
  }, props))) : null;
});

if (process.env.NODE_ENV !== "production") {
  DialogOverlay.propTypes = {
    initialFocusRef: function initialFocusRef() {}
  };
  DialogOverlay.displayName = "DialogOverlay";
} ////////////////////////////////////////////////////////////////////////////////


var DialogInner = React.forwardRef(function DialogInner(_ref2, forwardedRef) {
  var allowPinchZoom = _ref2.allowPinchZoom,
      initialFocusRef = _ref2.initialFocusRef,
      onClick = _ref2.onClick,
      _ref2$onDismiss = _ref2.onDismiss,
      onDismiss = _ref2$onDismiss === void 0 ? noop : _ref2$onDismiss,
      onMouseDown = _ref2.onMouseDown,
      onKeyDown = _ref2.onKeyDown,
      props = _objectWithoutPropertiesLoose(_ref2, ["allowPinchZoom", "initialFocusRef", "onClick", "onDismiss", "onMouseDown", "onKeyDown"]);

  var mouseDownTarget = React.useRef(null);
  var overlayNode = React.useRef(null);
  var ref = useForkedRef(overlayNode, forwardedRef);
  React.useEffect(function () {
    return createAriaHider(overlayNode.current);
  }, []);
  return React.createElement(FocusLock, {
    autoFocus: true,
    returnFocus: true,
    onActivation: function onActivation() {
      if (initialFocusRef && initialFocusRef.current) {
        initialFocusRef.current.focus();
      }
    }
  }, React.createElement(RemoveScroll, {
    allowPinchZoom: allowPinchZoom
  }, React.createElement("div", _extends({
    "data-reach-dialog-overlay": true,
    onClick: wrapEvent(onClick, function (event) {
      if (mouseDownTarget.current === event.target) {
        event.stopPropagation();
        onDismiss(event);
      }
    }),
    onMouseDown: wrapEvent(onMouseDown, function (event) {
      mouseDownTarget.current = event.target;
    }),
    onKeyDown: wrapEvent(onKeyDown, function (event) {
      if (event.key === "Escape") {
        event.stopPropagation();
        onDismiss(event);
      }
    }),
    ref: ref
  }, props))));
});

if (process.env.NODE_ENV !== "production") {
  DialogOverlay.displayName = "DialogOverlay";
} ////////////////////////////////////////////////////////////////////////////////


export var DialogContent = React.forwardRef(function DialogContent(_ref3, forwardedRef) {
  var onClick = _ref3.onClick,
      onKeyDown = _ref3.onKeyDown,
      props = _objectWithoutPropertiesLoose(_ref3, ["onClick", "onKeyDown"]);

  return React.createElement("div", _extends({
    role: "dialog",
    "aria-modal": "true",
    "data-reach-dialog-content": true,
    tabIndex: "-1",
    onClick: wrapEvent(onClick, function (event) {
      event.stopPropagation();
    }),
    ref: forwardedRef
  }, props));
});

if (process.env.NODE_ENV !== "production") {
  DialogContent.propTypes = {
    "aria-label": ariaLabelType,
    "aria-labelledby": ariaLabelType
  };
  DialogContent.displayName = "DialogContent";
} ////////////////////////////////////////////////////////////////////////////////


export var Dialog = React.forwardRef(function Dialog(_ref4, forwardedRef) {
  var isOpen = _ref4.isOpen,
      _ref4$onDismiss = _ref4.onDismiss,
      onDismiss = _ref4$onDismiss === void 0 ? noop : _ref4$onDismiss,
      initialFocusRef = _ref4.initialFocusRef,
      props = _objectWithoutPropertiesLoose(_ref4, ["isOpen", "onDismiss", "initialFocusRef"]);

  var ownRef = React.useRef(null);
  var ref = forwardedRef || ownRef;
  return React.createElement(DialogOverlay, {
    isOpen: isOpen,
    onDismiss: onDismiss,
    initialFocusRef: initialFocusRef
  }, React.createElement(DialogContent, _extends({
    ref: ref
  }, props)));
});

if (process.env.NODE_ENV !== "production") {
  Dialog.propTypes = {
    isOpen: bool,
    onDismiss: func,
    "aria-label": ariaLabelType,
    "aria-labelledby": ariaLabelType
  };
  Dialog.displayName = "Dialog";
}

export default Dialog; ////////////////////////////////////////////////////////////////////////////////

function createAriaHider(dialogNode) {
  var originalValues = [];
  var rootNodes = [];

  if (!dialogNode) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("A ref has not yet been attached to a dialog node when attempting to call `createAriaHider`.");
    }

    return noop;
  }

  Array.prototype.forEach.call(document.querySelectorAll("body > *"), function (node) {
    var portalNode = dialogNode.parentNode.parentNode.parentNode;

    if (node === portalNode) {
      return;
    }

    var attr = node.getAttribute("aria-hidden");
    var alreadyHidden = attr !== null && attr !== "false";

    if (alreadyHidden) {
      return;
    }

    originalValues.push(attr);
    rootNodes.push(node);
    node.setAttribute("aria-hidden", "true");
  });
  return function () {
    rootNodes.forEach(function (node, index) {
      var originalValue = originalValues[index];

      if (originalValue === null) {
        node.removeAttribute("aria-hidden");
      } else {
        node.setAttribute("aria-hidden", originalValue);
      }
    });
  };
}

function ariaLabelType(props, name, compName) {
  var details = "\nSee https://www.w3.org/TR/wai-aria/#aria-label for details.";

  if (!props["aria-label"] && !props["aria-labelledby"]) {
    return new Error("A <" + compName + "> must have either an `aria-label` or `aria-labelledby` prop.\n      " + details);
  }

  if (props["aria-label"] && props["aria-labelledby"]) {
    return new Error("You provided both `aria-label` and `aria-labelledby` props to a <" + compName + ">. If the a label for this component is visible on the screen, that label's component should be given a unique ID prop, and that ID should be passed as the `aria-labelledby` prop into <" + compName + ">. If the label cannot be determined programmatically from the content of the element, an alternative label should be provided as the `aria-label` prop, which will be used as an `aria-label` on the HTML tag." + details);
  }

  for (var _len = arguments.length, rest = new Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
    rest[_key - 3] = arguments[_key];
  }

  return string.apply(void 0, [name, props, compName].concat(rest));
}